import * as React from 'react';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PostPurchaseService from 'services/PostPurchaseService';
import { pacific, usd } from './shared';
import { ShippingResponse, ShippingStatus, UpsoldOrderShipping } from './types';

// The Shipping & margin tab: every upsold order in the page's period, what its
// acceptance cost us, and once ShipBob billed its boxes, what the upsell
// really added to the freight (collector shippingActuals.js): the order's
// boxes against the median box of orders with the same items without the
// upsell. Read on request; nothing here writes.

const STATUS: Record<ShippingStatus, { label: string; color: 'success' | 'default' | 'warning'; hint: string }> = {
  shipped: { label: 'Shipped', color: 'success', hint: 'Every box is billed by ShipBob: the margin uses the actual extra freight.' },
  'not shipped yet': { label: 'Not shipped yet', color: 'default', hint: 'ShipBob has not billed every box yet: the margin uses the estimate.' },
  'no comparable orders': {
    label: 'No comparable orders',
    color: 'warning',
    hint: "Shipped, but there is nothing to compare its box with (the order's items aren't in the warehouse yet): the margin uses the estimate.",
  },
};

type Filter = 'all' | ShippingStatus;
type SortKey =
  | 'order' | 'upsell' | 'accepted' | 'charge' | 'extraCogs' | 'fee' | 'estimatedFreight'
  | 'boxCost' | 'comparableBoxCost' | 'actualExtraFreight' | 'margin' | 'status';

// The margin the page counts for an order: its true one once shipped, else the estimate.
const marginOf = (order: UpsoldOrderShipping) => (order.status === 'shipped' ? order.trueMargin : order.estimatedMargin);

const SORTS: Record<SortKey, (order: UpsoldOrderShipping) => string | number | null> = {
  order: order => order.orderName ?? '',
  upsell: order => order.upsellId,
  accepted: order => order.acceptedAt,
  charge: order => order.charge,
  extraCogs: order => order.extraCogs,
  fee: order => order.fee,
  estimatedFreight: order => order.estimatedFreight,
  boxCost: order => order.boxCost,
  comparableBoxCost: order => order.comparableBoxCost,
  actualExtraFreight: order => order.actualExtraFreight,
  margin: marginOf,
  status: order => STATUS[order.status].label,
};
const PAGE_SIZES = [25, 50, 100];

const money = (value: number | null) => (value == null ? '—' : usd(value));
const sum = (orders: UpsoldOrderShipping[], value: (order: UpsoldOrderShipping) => number | null) =>
  orders.reduce<number | null>((total, order) => (total == null || value(order) == null ? null : total + (value(order) as number)), 0);

// Where the comparable box cost came from, short, and in full on hover.
function basisOf(order: UpsoldOrderShipping) {
  const n = order.comparableSample ?? 0;
  const same = `Median of ${n} orders with the same items, without the upsell, in the last 60 days`;
  if (order.comparableBasis === 'same_center_state') {
    return { short: `same FC + state · n=${n}`, long: `${same}, shipped from ${order.comparableCenter} to ${order.comparableState}.` };
  }
  if (order.comparableBasis === 'same_center_region') {
    return {
      short: `same FC + region · n=${n}`,
      long: `${same}, shipped from ${order.comparableCenter} to the ${order.comparableRegion} (fewer than 5 to ${order.state ?? 'its state'}).`,
    };
  }
  if (order.comparableBasis === 'same_center') {
    return {
      short: `same FC · n=${n}`,
      long: `${same}, shipped from ${order.comparableCenter} anywhere (fewer than 5 to ${order.state ?? 'its state'} or its region, so the destination isn't matched).`,
    };
  }
  if (order.comparableBasis === 'any_center') {
    return { short: `any FC · n=${n}`, long: `${same}, from any center to anywhere (fewer than 5 from this order's center).` };
  }
  if (order.comparableBasis === 'benchmark') {
    return {
      short: 'benchmark',
      long: `Only ${n} orders with the same items in the last 60 days (needs 5), so this is the average ShipBob cost of an order with as many units.`,
    };
  }
  return null;
}

function OrderRow({ order, adminUrl }: { order: UpsoldOrderShipping; adminUrl: (id: number) => string | undefined }) {
  const status = STATUS[order.status];
  const basis = basisOf(order);
  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <a href={adminUrl(order.orderId)} target="_blank" rel="noreferrer">{order.orderName ?? order.orderId}</a>
        {order.financialStatus && !['paid', 'partially_paid', 'authorized'].includes(order.financialStatus) && (
          <Chip size="small" label={order.financialStatus.replace('_', ' ')} sx={{ ml: 0.5 }} />
        )}
      </TableCell>
      <TableCell>
        {order.upsellId}
        {!order.confirmed && (
          <Tooltip title="Accepted, but not yet confirmed on the order (no swapped or added record).">
            <Chip size="small" variant="outlined" label="not confirmed" sx={{ ml: 0.5 }} />
          </Tooltip>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{pacific(order.acceptedAt)}</TableCell>
      <TableCell align="right">{money(order.charge)}</TableCell>
      <TableCell align="right">{money(order.extraCogs)}</TableCell>
      <TableCell align="right">{money(order.fee)}</TableCell>
      <TableCell align="right">{money(order.estimatedFreight)}</TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {money(order.boxCost)}
        <Typography variant="caption" component="div" color="text.secondary">
          {[order.centers.join(' + '), order.state ? `→ ${order.state}` : null, order.boxes > 1 ? `${order.boxes} boxes` : null].filter(Boolean).join(' · ') ||
            (order.boxes ? '' : 'no shipment yet')}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {money(order.comparableBoxCost)}
        {basis && (
          <Tooltip title={basis.long}>
            <Typography variant="caption" component="div" color={order.comparableBasis === 'benchmark' ? 'warning.main' : 'text.secondary'}>
              {basis.short}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="right">{money(order.actualExtraFreight)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 600 }}>
        {order.status === 'shipped' ? (
          money(order.trueMargin)
        ) : (
          <Tooltip title={`${status.label}: the estimated margin.`}>
            <span>{order.estimatedMargin == null ? '—' : `≈ ${usd(order.estimatedMargin)}`}</span>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={status.hint}>
          <Chip size="small" label={status.label} color={status.color} />
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export function ShippingTab({ service, period, store }: { service: PostPurchaseService; period: { from: string; to: string }; store: string }) {
  const [result, setResult] = React.useState<ShippingResponse | null | undefined>(undefined);
  const [filter, setFilter] = React.useState<Filter>('all');
  const [upsellFilter, setUpsellFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<{ key: SortKey; desc: boolean }>({ key: 'accepted', desc: true });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(PAGE_SIZES[0]);
  const { from, to } = period;
  React.useEffect(() => {
    let current = true;
    setResult(undefined);
    service.getShipping({ from, to }, store).then(response => current && setResult(response));
    return () => {
      current = false;
    };
  }, [service, from, to, store]);

  const orders = result?.orders ?? [];
  const count = (status: ShippingStatus) => orders.filter(order => order.status === status).length;
  const upsellIds = [...new Set(orders.map(order => order.upsellId))].sort();
  const needle = search.trim().toLowerCase();
  const shown = orders
    .filter(order => filter === 'all' || order.status === filter)
    .filter(order => upsellFilter === 'all' || order.upsellId === upsellFilter)
    .filter(order => !needle || [order.orderName ?? '', String(order.orderId), ...order.centers].some(text => text.toLowerCase().includes(needle)))
    .sort((a, b) => {
      const [x, y] = [SORTS[sort.key](a), SORTS[sort.key](b)];
      // Unknown values sort last either way.
      if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
      const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return sort.desc ? -order : order;
    });
  const lastPage = Math.max(0, Math.ceil(shown.length / pageSize) - 1);
  const shownPage = Math.min(page, lastPage);
  const choose = (next: () => void) => {
    next();
    setPage(0);
  };
  const header = (key: SortKey, label: string, hint?: string) => (
    <TableCell sortDirection={sort.key === key ? (sort.desc ? 'desc' : 'asc') : false} align={['order', 'upsell', 'accepted', 'status'].includes(key) ? 'left' : 'right'} sx={{ whiteSpace: 'nowrap' }}>
      <Tooltip title={hint ?? ''} describeChild>
        <TableSortLabel
          active={sort.key === key}
          direction={sort.key === key && !sort.desc ? 'asc' : 'desc'}
          onClick={() => choose(() => setSort(current => ({ key, desc: current.key === key ? !current.desc : true })))}
        >
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );
  const adminUrl = (orderId: number) =>
    result?.domain ? `https://admin.shopify.com/store/${result.domain.replace('.myshopify.com', '')}/orders/${orderId}` : undefined;

  // Totals over what the filters show: the margin the page counts (true where
  // shipped, estimated where not), with the split.
  const shipped = shown.filter(order => order.status === 'shipped');
  const unshipped = shown.filter(order => order.status !== 'shipped');
  const [shippedMargin, estimatedMargin] = [sum(shipped, order => order.trueMargin), sum(unshipped, order => order.estimatedMargin)];
  const totalMargin = shippedMargin == null || estimatedMargin == null ? null : shippedMargin + estimatedMargin;
  // Box cost, comparable box and extra freight exist for shipped orders only, so their totals are over those.
  const shippedTotal = (value: (order: UpsoldOrderShipping) => number | null, more = '') => (
    <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
      <Tooltip title={`Over the ${shipped.length} shipped orders.${more}`}>
        <span>{shipped.length ? money(sum(shipped, value)) : '—'}</span>
      </Tooltip>
      <Typography variant="caption" component="div" color="text.secondary">
        shipped ({shipped.length})
      </Typography>
    </TableCell>
  );
  const chips: { value: Filter; label: string }[] = [
    { value: 'all', label: `All (${orders.length})` },
    { value: 'shipped', label: `Shipped (${count('shipped')})` },
    { value: 'not shipped yet', label: `Not shipped yet (${count('not shipped yet')})` },
    // Kept while picked, so a period without any still shows which filter is on.
    ...(count('no comparable orders') || filter === 'no comparable orders' ? [{ value: 'no comparable orders' as Filter, label: `No comparable orders (${count('no comparable orders')})` }] : []),
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-label="shipping and margin">
      <Typography variant="subtitle1" fontWeight={600}>Shipping & margin</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Every upsold order {from === to ? `on ${from}` : `from ${from} to ${to}`} (Pacific): what accepting cost us, and once
        ShipBob has billed its boxes, what the upsell really added to the freight. <strong>Extra freight</strong> is what ShipBob
        billed for the order's boxes less the median ShipBob cost, over the last 60 days, of orders with exactly the same
        items without the upsell: the first with 5 or more of the same fulfillment center to the same state, the same center
        to the same region, the same center, any center, else the average order with as many units. True margin = charge − extra product cost − card fee − extra freight; before
        refunds. Until an order ships its margin is the estimate (≈).
      </Typography>
      {result?.problem && <Alert severity="warning" sx={{ mb: 1.5 }}>{result.problem}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {chips.map(chip => (
          <Chip
            key={chip.value}
            label={chip.label}
            color={filter === chip.value ? 'primary' : 'default'}
            variant={filter === chip.value ? 'filled' : 'outlined'}
            onClick={() => choose(() => setFilter(chip.value))}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        <Select size="small" value={upsellFilter} onChange={event => choose(() => setUpsellFilter(String(event.target.value)))} inputProps={{ 'aria-label': 'upsell' }}>
          <MenuItem value="all">All upsells</MenuItem>
          {upsellIds.map(id => (
            <MenuItem key={id} value={id}>{id}</MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          placeholder="Search order # or center"
          value={search}
          onChange={event => choose(() => setSearch(event.target.value))}
          inputProps={{ 'aria-label': 'search upsold orders' }}
          sx={{ minWidth: 240 }}
        />
      </Box>
      {result === undefined && <LinearProgress aria-label="loading upsold orders" />}
      {result === null && <Alert severity="error">Could not load the upsold orders' shipping.</Alert>}
      {result && !shown.length && <Typography variant="body2" color="text.secondary">No upsold orders here.</Typography>}
      {result && shown.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="upsold orders">
            <TableHead>
              <TableRow>
                {header('order', 'Order')}
                {header('upsell', 'Upsell')}
                {header('accepted', 'Accepted (Pacific)')}
                {header('charge', 'Charge', 'The extra charged, before tax.')}
                {header('extraCogs', 'Extra COGS', "A swap's target units less the trigger units they replace; an add-on's products (Shopify COGS, today's).")}
                {header('fee', 'Fee', 'The card fee on the upsell charge: 2.25% + 30¢.')}
                {header('estimatedFreight', 'Est. freight', 'The extra freight the margins estimate before an order ships.')}
                {header('boxCost', 'Box cost', "What ShipBob billed for all of this order's boxes.")}
                {header('comparableBoxCost', 'Comparable box', 'What the same order would have cost to ship without the upsell. Hover the basis for how it was measured.')}
                {header('actualExtraFreight', 'Extra freight', 'Box cost − comparable box.')}
                {header('margin', 'Margin', 'True margin once shipped, else the estimate (≈).')}
                {header('status', 'Status')}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.slice(shownPage * pageSize, (shownPage + 1) * pageSize).map(order => (
                <OrderRow key={`${order.orderId}:${order.upsellId}`} order={order} adminUrl={adminUrl} />
              ))}
            </TableBody>
            <TableFooter>
              <TableRow aria-label="totals">
                <TableCell colSpan={3} sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {shown.length} {shown.length === 1 ? 'order' : 'orders'}{filter !== 'all' || upsellFilter !== 'all' || needle ? ' shown' : ''}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>{money(sum(shown, order => order.charge))}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>{money(sum(shown, order => order.extraCogs))}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>{money(sum(shown, order => order.fee))}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>{money(sum(shown, order => order.estimatedFreight))}</TableCell>
                {shippedTotal(order => order.boxCost)}
                {shippedTotal(order => order.comparableBoxCost)}
                {shippedTotal(order => order.actualExtraFreight, ` Their estimate was ${money(sum(shipped, order => order.estimatedFreight))}.`)}
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
                  {money(totalMargin)}
                  <Typography variant="caption" component="div" color="text.secondary">
                    {money(shippedMargin)} actual ({shipped.length}) + ≈{money(estimatedMargin)} est. ({unshipped.length})
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
          <TablePagination
            component="div"
            count={shown.length}
            page={shownPage}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={event => choose(() => setPageSize(Number(event.target.value)))}
          />
        </Box>
      )}
    </Paper>
  );
}
